<template>
    <tailwind-layout title="Campaign Attribution">
        <div class="space-y-6">
            <BridgeBackLink />
            <header>
                <h1 class="text-2xl font-bold tracking-tight text-rent-risk-blue-400">Campaign Attribution</h1>
                <p class="mt-1 text-sm text-gray-600">
                    Paid subscriptions driven per marketing email ({{ 'utm_content' }}). Last-touch within
                    {{ windowHours }} hours of a click. First-time conversions only — plan upgrades are not yet counted.
                </p>
                <Link :href="route('bridge.campaign-attribution.subjects.index')" class="mt-2 inline-block text-sm font-medium text-rent-risk-blue-400 hover:underline">
                    Edit email subjects
                </Link>
            </header>

            <!-- Totals -->
            <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Clicks</p>
                    <p class="mt-1 text-2xl font-bold text-gray-900">{{ totals.clicks }}</p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Unique Users</p>
                    <p class="mt-1 text-2xl font-bold text-gray-900">{{ totals.unique_users }}</p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Conversions</p>
                    <p class="mt-1 text-2xl font-bold text-gray-900">{{ totals.conversions }}</p>
                </div>
                <div class="rounded-xl border border-rent-risk-blue-100 bg-rent-risk-blue-50 p-4 shadow-sm">
                    <p class="text-xs font-semibold uppercase tracking-wider text-rent-risk-blue-400">Revenue</p>
                    <p class="mt-1 text-2xl font-bold text-rent-risk-blue-400">{{ formatCurrency(totals.revenue) }}</p>
                </div>
            </div>

            <!-- Date range filter (by click date) -->
            <div class="flex flex-wrap items-end gap-4">
                <div>
                    <label for="from" class="block text-xs font-semibold uppercase tracking-wider text-gray-400">Clicked from</label>
                    <input id="from" v-model="form.from" type="date" class="mt-1 block w-full rounded-md border-gray-100 py-2 text-sm shadow-sm drop-shadow focus:border-rent-risk-blue-400 focus:outline-none focus:ring-rent-risk-blue-400">
                </div>
                <div>
                    <label for="to" class="block text-xs font-semibold uppercase tracking-wider text-gray-400">Clicked to</label>
                    <input id="to" v-model="form.to" type="date" class="mt-1 block w-full rounded-md border-gray-100 py-2 text-sm shadow-sm drop-shadow focus:border-rent-risk-blue-400 focus:outline-none focus:ring-rent-risk-blue-400">
                </div>
                <button v-if="form.from || form.to" type="button" class="rounded-md px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700" @click="clearFilters">
                    Clear
                </button>
            </div>
            <p class="-mt-2 text-xs text-gray-400">
                Filtered by click date. A subscription started within {{ windowHours }} hours of a click is credited to that click's email.
            </p>

            <!-- Desktop table -->
            <div class="rounded-md shadow-none overflow-x-auto lg:shadow">
                <div class="inline-block min-w-full align-middle">
                    <div class="overflow-hidden shadow lg:border">
                        <table class="hidden min-w-full divide-y divide-gray-300 lg:table">
                            <thead class="bg-blue-50">
                                <tr>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">Email Subject</th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">utm_content</th>
                                    <th scope="col" class="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider">Clicks</th>
                                    <th scope="col" class="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider">Unique Users</th>
                                    <th scope="col" class="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider">Conversions</th>
                                    <th scope="col" class="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider">Conv. Rate</th>
                                    <th scope="col" class="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider">Revenue</th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">By Plan</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in rows" :key="row.utm_content" class="bg-white hover:bg-gray-100">
                                    <td class="px-6 py-4 text-sm font-medium text-gray-900">
                                        <span v-if="row.email_subject">{{ row.email_subject }}</span>
                                        <span v-else class="text-gray-400" title="Not mapped yet — set it under Edit email subjects">—</span>
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-500">{{ row.utm_content }}</td>
                                    <td class="px-6 py-4 text-center text-sm text-gray-500">{{ row.clicks }}</td>
                                    <td class="px-6 py-4 text-center text-sm text-gray-500">{{ row.unique_users }}</td>
                                    <td class="px-6 py-4 text-center text-sm font-semibold text-gray-900">{{ row.conversions }}</td>
                                    <td class="px-6 py-4 text-center text-sm text-gray-500">{{ row.conversion_rate }}%</td>
                                    <td class="px-6 py-4 text-right text-sm font-semibold text-gray-900">{{ formatCurrency(row.revenue) }}</td>
                                    <td class="px-6 py-4 text-sm text-gray-500">
                                        <div v-for="(stat, type) in row.by_type" :key="type" class="whitespace-nowrap">
                                            {{ planLabel(type) }}: {{ stat.conversions }} ({{ formatCurrency(stat.revenue) }})
                                        </div>
                                        <span v-if="!Object.keys(row.by_type).length" class="text-gray-400">—</span>
                                    </td>
                                </tr>
                                <tr v-show="!rows.length">
                                    <td colspan="7" class="px-6 py-4 text-center text-sm font-medium text-dark-gray-400">
                                        No campaign clicks in this range yet.
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Mobile cards -->
                        <div class="space-y-4 p-4 lg:hidden">
                            <div v-for="row in rows" :key="row.utm_content" class="rounded-lg border bg-white p-4 shadow">
                                <div class="mb-2 flex items-start justify-between">
                                    <div>
                                        <h3 class="text-sm font-semibold text-gray-900">{{ row.email_subject || row.utm_content }}</h3>
                                        <p v-if="row.email_subject" class="text-xs text-gray-400">{{ row.utm_content }}</p>
                                    </div>
                                    <span class="ml-2 text-sm font-semibold text-rent-risk-blue-400">{{ formatCurrency(row.revenue) }}</span>
                                </div>
                                <div class="space-y-1 text-xs text-gray-600">
                                    <div><strong>Clicks:</strong> {{ row.clicks }} ({{ row.unique_users }} users)</div>
                                    <div><strong>Conversions:</strong> {{ row.conversions }} ({{ row.conversion_rate }}%)</div>
                                    <div v-for="(stat, type) in row.by_type" :key="type">
                                        <strong>{{ planLabel(type) }}:</strong> {{ stat.conversions }} ({{ formatCurrency(stat.revenue) }})
                                    </div>
                                </div>
                            </div>
                            <div v-if="!rows.length" class="py-8 text-center text-gray-500">
                                No campaign clicks in this range yet.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </tailwind-layout>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import BridgeBackLink from '@/Shared/BridgeBackLink.vue'

const props = defineProps({
    rows: Array,
    totals: Object,
    windowHours: Number,
    filters: Object,
})

const form = ref({
    from: props.filters?.from || '',
    to: props.filters?.to || '',
})

const planLabels = {
    'rentrisk-protect': 'RiskGuard',
    'rentrisk-protect-pro': 'RiskGuard Pro',
}

const planLabel = (type) => planLabels[type] || type

const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0)

const clearFilters = () => {
    form.value.from = ''
    form.value.to = ''
}

const applyFilters = throttle(() => {
    router.get(route('bridge.campaign-attribution.index'), pickBy(form.value), { preserveState: true })
}, 150)

watch(() => form.value, applyFilters, { deep: true })
</script>
